import EditIcon from '@components/icons/Edit';
import SaveIcon from '@components/icons/SaveIcon';
import CardWrapper from '@components/UI/CardWrapper';
import { useBitrix } from '@context/useBitrixUser/useBitrix';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormControl, FormControlLabel, FormHelperText, Popper } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RedoIcon from '@mui/icons-material/Redo';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { companyProfileService } from '@services/requests/companyProfileService';
import { ItemSelector } from '@services/requests/companyProfileService/interface';
import { MainData } from '@services/requests/companyProfileService/types/mainData';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ChangeNotification } from '../../ChangeNotification';
import { bankDetailsFormSchema } from './schemas';
import { BankDetailsItemStyledContainer } from './StyledComponents';
import { BankDetailsFormFields } from './types';

interface BankDetailsItemProps {
  bankDetailsItem: BankDetailsFormFields;
  bankDetails: BankDetailsFormFields[];
  index: number;
  isAllowEdit: boolean;
  mainData: MainData;
  onRemoveBankDetails: (bankIndex: number, id: string, onError: (errors: string[]) => void) => void;
  //   onChangeBankDetails: <T extends keyof BankDetailsFormFields>(
  //     index: number,
  //     key: T,
  //     value: BankDetailsFormFields[T]
  //   ) => void;
  onRemoveDefaultDetails: (excludeIndex: number) => void;
}

const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom"
      style={{ width: props.anchorEl.clientWidth, minWidth: 'fit-content' }}
    />
  );
};

function BankDetailsItem({
  bankDetailsItem,
  bankDetails,
  index,
  mainData,
  isAllowEdit,
  onRemoveBankDetails,
  onRemoveDefaultDetails,
}: BankDetailsItemProps) {
  const [isChangeNotificationOpen, setIsChangeNotificationOpen] = useState<boolean>(false);

  const { useBitrixUser } = useBitrix();
  const [{ bitrixUser }] = useBitrixUser().useState();

  const form = useForm<BankDetailsFormFields>({
    resolver: zodResolver(bankDetailsFormSchema),
    defaultValues: bankDetailsItem,
  });

  const {
    handleSubmit,
    reset,
    control,
    watch,
    getValues,
    setError,
    formState: { errors, defaultValues },
  } = form;

  const { fields } = mainData.sections.bank_requisites;

  const id = watch('id');
  const bankName = watch('bankName');
  const currency = watch('currency');
  const isDefaultDetailsExist = bankDetails.some((item) => item.defaultDetails);

  useEffect(() => {
    reset({ ...getValues(), defaultDetails: bankDetailsItem.defaultDetails });
  }, [bankDetailsItem.defaultDetails]);

  const isNewBank = id.includes('new-bank-details');

  const [isEditMode, setIsEditMode] = useState<boolean>(isNewBank);

  const submitBankDetailsFormHandler = async (data: BankDetailsFormFields) => {
    if (!isAllowEdit) {
      return;
    }
    const result = await companyProfileService().SaveBankDetails(bitrixUser.id, data);

    if ('errors' in result) {
      setError('defaultDetails', { message: result.errors[0] });
      return;
    }

    if (!result.success) {
      return;
    }

    const newDefaultValues = { ...getValues() };
    if (isNewBank) {
      newDefaultValues.id = result.fields.id.value.toString();
    }

    if (data.defaultDetails && !defaultValues.defaultDetails) {
      onRemoveDefaultDetails(index);
    }

    setIsEditMode(false);
    setIsChangeNotificationOpen(true);
    reset(newDefaultValues);
  };

  const changeEditModeHandler = (value: boolean) => {
    if (!isAllowEdit) {
      return;
    }
    setIsEditMode(value);
  };

  const cancelEditingHandler = async () => {
    if (!isAllowEdit) {
      return;
    }
    // reset({ ...defaultBankDetails.current });
    // onChangeBankDetails(index, 'defaultDetails', defaultBankDetails.current.defaultDetails);
    // setIsEditMode(false);
    reset({ ...defaultValues, defaultDetails: defaultValues.defaultDetails });
    // onChangeBankDetails(index, 'defaultDetails', defaultValues.defaultDetails);
    setIsEditMode(false);

    // old version
    // reset();
    // setIsEditMode(false);
    // if (defaultBankDetails.current) {
    //   if (isDefaultDetailsExist && !isDefaultDetails && getValues().defaultDetails) {
    //     setValue('defaultDetails', false);
    //     onChangeBankDetails(index, 'defaultDetails', false);
    //     defaultBankDetails.current = { ...getValues(), defaultDetails: false };
    //     return;
    //   }
    //   onChangeBankDetails(index, 'defaultDetails', defaultBankDetails.current.defaultDetails);
    // }

    //new version
    // const newDefaultDetailsValue =
    //   isDefaultDetailsExist && isDefaultDetails && getValues().defaultDetails
    //     ? true
    //     : defaultBankDetails.current.defaultDetails;

    // setIsEditMode(false);
    // if (defaultBankDetails.current) {
    //   if (isDefaultDetailsExist && !isDefaultDetails && defaultBankDetails.current.defaultDetails) {
    //     companyProfileService().SaveBankDetails('52', {
    //       ...defaultBankDetails.current,
    //       defaultDetails: false,
    //     });
    //     reset({ ...defaultBankDetails.current, defaultDetails: false });
    //     onChangeBankDetails(index, 'defaultDetails', false);
    //     defaultBankDetails.current = { ...getValues(), defaultDetails: false };
    //     return;
    //   }

    //   onChangeBankDetails(index, 'defaultDetails', newDefaultDetailsValue);
    //   companyProfileService().SaveBankDetails('52', {
    //     ...defaultBankDetails.current,
    //     defaultDetails: newDefaultDetailsValue,
    //   });
    //   reset({ ...defaultBankDetails.current, defaultDetails: newDefaultDetailsValue });
    //   defaultBankDetails.current = {
    //     ...defaultBankDetails.current,
    //     defaultDetails: newDefaultDetailsValue,
    //   };
    // }
  };

  return (
    <>
      <BankDetailsItemStyledContainer>
        <form onSubmit={handleSubmit(submitBankDetailsFormHandler)}>
          <CardWrapper sx={{ padding: '25px 40px' }} className="card">
            <Typography variant={'h3'} className={'module-title'}>
              {mainData.sections.bank_requisites.caption}
              {!!bankName && bankName.length > 0 ? ` ${bankName}` : ''}
              {!!currency && currency.length > 0 ? `, ${currency}` : ''}:
            </Typography>
            <Controller
              control={control}
              name="bankName"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  type="text"
                  className="comment-field"
                  size={'medium'}
                  disabled={!isEditMode}
                  label={fields[0].name.title}
                  variant="outlined"
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  value={value || ''}
                  error={!!errors.bankName}
                  InputProps={{ classes: { input: 'browser-default' } }}
                />
              )}
            />
            <Controller
              control={control}
              name="bankAddress"
              render={({ field: { onChange, value, ref, onBlur, name } }) => (
                <TextField
                  fullWidth
                  type="text"
                  className="comment-field"
                  size={'medium'}
                  disabled={!isEditMode}
                  label={fields[0].address.title}
                  variant="outlined"
                  multiline
                  minRows={2}
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  value={value || ''}
                  error={!!errors.bankAddress}
                  InputProps={{ classes: { inputMultiline: 'browser-default' } }}
                />
              )}
            />
            <div className="currency-account-wrapper">
              <Controller
                control={control}
                name="currentAccount"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="currency-account-wrapper__main"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields[0].acc_num.title}
                    variant="outlined"
                    name={name}
                    onChange={(event) =>
                      /^[0-9]*$/.test(event.target.value) && onChange(event.target.value)
                    }
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.currentAccount}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="correspondentAccount"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="currency-account-wrapper__secondary"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields[0].cor_acc_num.title}
                    variant="outlined"
                    name={name}
                    onChange={(event) =>
                      /^[0-9]*$/.test(event.target.value) && onChange(event.target.value)
                    }
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.correspondentAccount}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
            </div>
            <div className="bank-data-wrapper">
              <Controller
                control={control}
                name="currency"
                render={({ field: { onChange, value, ref, onBlur } }) => (
                  <Autocomplete
                    disablePortal
                    fullWidth
                    className="currency"
                    options={fields[0].acc_currency.items ?? []}
                    disabled={!isEditMode}
                    onChange={(_, value) => onChange(value ? value.code : null)}
                    PopperComponent={CustomPopper}
                    onBlur={onBlur}
                    ref={ref}
                    value={
                      value
                        ? (fields[0].acc_currency.items ?? []).find((c) => c.code === value) ?? null
                        : null
                    }
                    popupIcon={<KeyboardArrowDownIcon />}
                    classes={{ input: 'browser-default', root: 'bank-data-wrapper__item' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={fields[0].acc_currency.title}
                        error={!!errors.currency}
                      />
                    )}
                    renderOption={(props, option) => {
                      return (
                        <MenuItem
                          {...props}
                          value={option.code}
                          key={option.code}
                          className="a-service-field-selector-option--root"
                        >
                          {option.title}
                        </MenuItem>
                      );
                    }}
                    getOptionLabel={(option: ItemSelector) => option.title}
                  />
                )}
              />
              <Controller
                control={control}
                name="bik"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="bank-data-wrapper__item bank-data-wrapper__item-bik"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields[0].bik.title}
                    variant="outlined"
                    name={name}
                    onChange={(event) =>
                      /^[0-9]*$/.test(event.target.value) && onChange(event.target.value)
                    }
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.bik}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
              <Controller
                control={control}
                name="swift"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  <TextField
                    fullWidth
                    type="text"
                    className="bank-data-wrapper__item"
                    size={'medium'}
                    disabled={!isEditMode}
                    label={fields[0].swift.title}
                    variant="outlined"
                    name={name}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    value={value || ''}
                    error={!!errors.swift}
                    InputProps={{ classes: { input: 'browser-default' } }}
                  />
                )}
              />
            </div>
            <div className="module-control-wrapper">
              <Controller
                control={control}
                name="defaultDetails"
                render={({ field: { onChange, value, ref, onBlur, name } }) => (
                  //   <Tooltip
                  //     title={
                  //       isDefaultDetailsExist &&
                  //       !isDefaultDetails &&
                  //       isEditMode &&
                  //       'Сначала нужно убрать опцию с существующего реквизита по умолчанию'
                  //     }
                  //   >
                  <FormControl
                    // required
                    error={!!errors.defaultDetails}
                    // component="fieldset"
                    // sx={{ m: 3 }}
                    variant="standard"
                  >
                    <FormControlLabel
                      className="a-service-field-checkbox"
                      classes={{
                        label: 'checkbox-label',
                      }}
                      name={name}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        onChange(event.target.checked);
                        //   onChangeBankDetails(index, 'defaultDetails', event.target.checked);
                      }}
                      onBlur={onBlur}
                      ref={ref}
                      //   disabled={!isEditMode || (isDefaultDetailsExist && !isDefaultDetails)}
                      disabled={!isEditMode}
                      value={value}
                      checked={value}
                      control={
                        <Checkbox
                          size={'small'}
                          //   disabled={!isEditMode || (isDefaultDetailsExist && !isDefaultDetails)}
                          disabled={!isEditMode}
                        />
                      }
                      label={fields[0].is_default.title}
                    />
                    {!!errors.defaultDetails && (
                      <FormHelperText>{errors.defaultDetails.message}</FormHelperText>
                    )}
                  </FormControl>
                  //   </Tooltip>
                )}
              />
              <div className="module-control-wrapper__buttons">
                {(isNewBank || isEditMode) && (
                  //   <Tooltip
                  //     title={
                  //       !isDefaultDetailsExist
                  //         ? 'Выберите один из реквизитов по умолчанию прежде чем сохранить'
                  //         : mainData.messages.btn_save
                  //     }
                  //   >

                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Tooltip title={mainData.messages.btn_save}>
                      <IconButton
                        color="primary"
                        size={'small'}
                        sx={{ marginRight: '2px' }}
                        type="submit"
                        disabled={!isDefaultDetailsExist || !isAllowEdit}
                      >
                        <SaveIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                  //   </Tooltip>
                )}
                {!isNewBank && isEditMode && (
                  <Tooltip title={mainData.messages.btn_undo_edit}>
                    <IconButton
                      color="primary"
                      size={'small'}
                      sx={{ marginRight: '2px' }}
                      disabled={!isAllowEdit}
                      onClick={cancelEditingHandler}
                    >
                      <RedoIcon fontSize={'small'} sx={{ color: '#707070' }} />
                    </IconButton>
                  </Tooltip>
                )}
                {!isNewBank && !isEditMode && (
                  <Tooltip title={mainData.messages.btn_edit}>
                    <IconButton
                      color="primary"
                      size={'small'}
                      disabled={!isAllowEdit}
                      onClick={() => changeEditModeHandler(true)}
                    >
                      <EditIcon fontSize={'small'} sx={{ color: '#3460B4' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={mainData.messages.btn_delete}>
                  <IconButton
                    color="primary"
                    size="small"
                    disabled={bankDetails.length === 1 || !isAllowEdit}
                    onClick={() => {
                      if (!isAllowEdit) {
                        return;
                      }
                      onRemoveBankDetails(index, bankDetailsItem.id, (errors) => {
                        if (errors.length >= 1) {
                          setError('defaultDetails', { message: errors[0] });
                        }
                      });
                    }}
                  >
                    <CloseIcon fontSize={'small'} sx={{ color: '#F15642' }} />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </CardWrapper>
        </form>
      </BankDetailsItemStyledContainer>
      <ChangeNotification
        needReviewType={false}
        isOpen={isChangeNotificationOpen}
        onClose={() => setIsChangeNotificationOpen(false)}
      />
    </>
  );
}
export { BankDetailsItem };
